import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { BusinessPartnerNavigationComponent } from 'app/shared/components/business-partner-navigation/business-partner-navigation.component';
import { BusinessPartnerNavigationData } from 'app/shared/components/business-partner-navigation/interfaces/business-partner-navigation.interfaces';

/**
 * Business Partner Simple Cell Renderer Component
 *
 * Renders business partner information with code (bold) and name.
 * Simpler version for displaying a single business partner object.
 *
 * Features:
 * - Displays external code in bold on first line
 * - Displays name in secondary font below code
 * - Falls back to "-" for null/undefined values
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'businessPartner',
 *   headerName: 'Socio de Negocio',
 *   cellRenderer: BusinessPartnerSimpleCellRendererComponent,
 *   sortable: true,
 *   minWidth: 250
 * }
 * ```
 *
 * Output examples:
 * - "BP001
 *    Distribuidora ABC S.A."
 * - "CUST-123
 *    Cliente XYZ"
 */
@Component({
  selector: 'app-business-partner-simple-cell-renderer',
  standalone: true,
  imports: [CommonModule, BusinessPartnerNavigationComponent],
  template: `
    <div class="text-content flex flex-col gap-0 leading-tight">
      <div class="flex items-center gap-2">
        <app-business-partner-navigation
          [businessPartner]="businessPartnerData"
          [displayMode]="'code'"
          [openInNewTab]="true"
          [showIcon]="true"
          [iconPosition]="'right'"
          [interactionMode]="'icon-only'"
          [showText]="true"
          [customClass]="'font-bold'"
        />
      </div>
      <span class="font-descrip leading-tight">{{ name }}</span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    .f-10 {
      font-size: 10px;
    }
    .font-descrip {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
    }
    :host-context(.dark) .font-descrip {
      color: rgba(255, 255, 255, 0.6);
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessPartnerSimpleCellRendererComponent implements CellRenderer<unknown, BusinessPartnerNavigationData> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<unknown, BusinessPartnerNavigationData>;

  /**
   * Get business partner data object for navigation component
   */
  get businessPartnerData(): BusinessPartnerNavigationData | null {
    return this.params?.value || null;
  }

  /**
   * Get business partner name
   */
  get name(): string {
    const bp = this.params?.value;
    return bp?.name || '-';
  }
}
